import { Badge } from '@/components/ui/badge'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { buildPublicPlayerProfileUrl } from '@/lib/arena'
import {
  ARENA_SEO_LANGUAGES,
  applyArenaDynamicHead,
  arenaDirectoryPath,
  arenaLanguageTag,
  arenaSeoOrigin,
  arenaSeoRobots,
} from '@/lib/arena-dynamic-seo'
import { useI18n } from '@/lib/i18n'
import { getStartedGameRoute } from '@/lib/started-game-route'
import {
  formatGameSettingDisplay,
  parseGameSettingToPlayerConfig,
} from '@/lib/play'
import { useTRPC } from '@/lib/trpc'
import { withLanguagePath } from '@/lib/url-language-utils'
import { readSettingMode, writeSetting } from '@repo/sdk'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useLayoutEffect } from 'react'
import { useSearchParams } from 'react-router-dom'

import { cn } from '@/lib/utils'
import {
  CalendarDays,
  Clock3,
  HeartHandshake,
  Swords,
  Trophy,
  UserRound,
  UsersRound,
} from 'lucide-react'

const DIRECTORY_STALE_TIME_MS = 24 * 60 * 60 * 1000

const LOCALES: Record<string, string> = {
  ar: 'ar-SA',
  en: 'en-US',
  es: 'es-ES',
  fr: 'fr-FR',
  ja: 'ja-JP',
  ko: 'ko-KR',
  tw: 'zh-TW',
  zh: 'zh-CN',
}

type Translate = (
  key: string,
  params?: Record<string, string | number>,
) => string

export interface DirectoryGame {
  id: string
  replayPath: string
  language: string
  isEnglishFallback: boolean
  gameSetting: string
  winner: string | null
  startedAt: Date | string
  playerCount: number
  humanPlayers: Array<{ username: string }>
}

export interface DirectorySupporter {
  username: string
  amountUsdCents: number
}

export interface DirectoryGamesPlayer {
  username: string
  gamesPlayed: number
}

const EMPTY_GAMES_PLAYERS: DirectoryGamesPlayer[] = []

function translatedOrNull(t: Translate, key: string) {
  const translated = t(key)
  return translated === key ? null : translated
}

export function formatDirectoryGameSetting(
  gameSetting: string,
  playerCount: number,
  t: Translate,
) {
  const parsed = parseGameSettingToPlayerConfig(gameSetting)
  const normalizedSetting = writeSetting({ kind: 'strip-archive', setting: gameSetting })
  const title = readSettingMode(gameSetting).terminalSuffix === 'R'
    ? t('gameSettings.G10_ULTIMATE_TRIAL')
    : (translatedOrNull(t, `gameSettings.${normalizedSetting}`)
      ?? translatedOrNull(t, `gameSettings.${gameSetting}`)
      ?? t('gameArchive.playerGameTitle', { count: playerCount }))

  return {
    title,
    playerCountLabel: t('gameArchive.playerCount', { count: playerCount }),
    composition: parsed
      ? formatGameSettingDisplay(parsed, t)
      : t('gameArchive.configurationUnavailable'),
  }
}

export function formatUsd(amountUsdCents: number, language: string) {
  return new Intl.NumberFormat(LOCALES[language] ?? LOCALES.en, {
    style: 'currency',
    currency: 'USD',
  }).format(amountUsdCents / 100)
}

export function formatDirectoryDate(
  value: Date | string,
  language: string,
  includeTime = false,
) {
  const date = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(date.getTime()))
    return ''

  return new Intl.DateTimeFormat(LOCALES[language] ?? LOCALES.en, {
    dateStyle: 'medium',
    ...(includeTime ? { timeStyle: 'short' as const } : {}),
    timeZone: 'UTC',
  }).format(date)
}

function winnerLabel(winner: string | null, t: Translate) {
  if (winner === 'good')
    return t('gameArchive.winner.good')
  if (winner === 'evil')
    return t('gameArchive.winner.evil')
  return t('gameArchive.winner.unknown')
}

export function GameArchiveHeader({
  generatedAt,
  language,
  t,
}: {
  generatedAt?: Date | string
  language: string
  t: Translate
}) {
  return (
    <header className="mb-8 max-w-3xl">
      <div className="mb-3 inline-flex items-center gap-2 rounded-full border bg-muted/40 px-3 py-1 text-xs font-medium text-muted-foreground">
        <UsersRound className="size-3.5" aria-hidden />
        {t('gameArchive.humanGamesBadge')}
      </div>
      <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">
        {t('gameArchive.title')}
      </h1>
      {generatedAt && (
        <p className="mt-3 flex items-center gap-1.5 text-xs text-muted-foreground">
          <Clock3 className="size-3.5" aria-hidden />
          {t('gameArchive.cachedAt', {
            date: formatDirectoryDate(generatedAt, language, true),
          })}
        </p>
      )}
    </header>
  )
}

export function GameArchivePage() {
  const trpc = useTRPC()
  const { language, t, isLoading: translationsLoading } = useI18n()
  const [searchParams, setSearchParams] = useSearchParams()
  const page = Math.min(
    10_000,
    Math.max(1, Number.parseInt(searchParams.get('page') ?? '1', 10) || 1),
  )
  const path = withLanguagePath('/games', language)
  const hasServerPrelude = typeof document !== 'undefined'
    && document.getElementById('seo-game-directory-prelude') !== null

  const pageHref = (target: number) => {
    return arenaDirectoryPath(language, target)
  }
  const goToPage = (target: number) => {
    setSearchParams(target <= 1 ? {} : { page: String(target) })
    window.scrollTo({ top: 0 })
  }

  const { data, isLoading, isError } = useQuery({
    ...trpc.publicGameDirectoryRouter.get.queryOptions({ language, page }),
    staleTime: DIRECTORY_STALE_TIME_MS,
    refetchOnWindowFocus: false,
  })

  const pageReady = !!data && data.page === page && !translationsLoading
  const onCanonicalPath = typeof window !== 'undefined'
    && window.location.pathname === path

  useEffect(() => {
    if (!data || translationsLoading)
      return

    const effectivePath = arenaDirectoryPath(language, data.page)
    if (window.location.pathname !== path) {
      window.location.replace(`${window.location.origin}${effectivePath}`)
      return
    }

    const expectedSearch = data.page > 1 ? `?page=${data.page}` : ''
    if (window.location.search !== expectedSearch)
      setSearchParams(data.page > 1 ? { page: String(data.page) } : {}, { replace: true })
  }, [data, language, path, setSearchParams, translationsLoading])

  useLayoutEffect(() => {
    const prelude = document.getElementById('seo-game-directory-prelude')
    if (!pageReady || !onCanonicalPath) {
      if (prelude)
        return
      return applyArenaDynamicHead({
        title: `${t('gameArchive.title')} | Mentiss`,
        description: t('gameArchive.humanGamesBadge'),
        language: arenaLanguageTag(language),
        robots: arenaSeoRobots(false),
      })
    }

    const origin = arenaSeoOrigin()
    const canonical = `${origin}${arenaDirectoryPath(language, page)}`
    const availableLanguages = data.isEmpty ? []
      : ARENA_SEO_LANGUAGES.filter(([code]) =>
          data.pageCountByLanguage[code] >= page)
    const alternates = availableLanguages.map(([code, hreflang]) => ({
      hreflang,
      href: `${origin}${arenaDirectoryPath(code, page)}`,
    }))
    if (!data.isEmpty && data.pageCountByLanguage.en >= page) {
      alternates.push({
        hreflang: 'x-default',
        href: `${origin}${arenaDirectoryPath('en', page)}`,
      })
    }
    const title = `${t('gameArchive.title')} | Mentiss`
    const description = t('gameArchive.humanGamesBadge')
    const cleanup = applyArenaDynamicHead({
      title,
      description,
      language: arenaLanguageTag(language),
      robots: arenaSeoRobots(!data.isEmpty),
      canonical,
      alternates,
      previous: page > 1
        ? `${origin}${arenaDirectoryPath(language, page - 1)}` : undefined,
      next: page < data.pageCount
        ? `${origin}${arenaDirectoryPath(language, page + 1)}` : undefined,
      structuredDataId: 'seo-game-directory-structured-data',
      structuredData: {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        'name': title,
        description,
        'url': canonical,
      },
    })
    if (prelude?.dataset.language === language
      && Number(prelude.dataset.page) === page) {
      prelude.remove()
    }
    return cleanup
  }, [data, language, onCanonicalPath, page, pageReady, t])

  if (hasServerPrelude && !pageReady)
    return null

  return (
    <main className="container mx-auto px-4 py-8 sm:py-10">
      <GameArchiveHeader
        generatedAt={data?.generatedAt}
        language={language}
        t={t}
      />

      <div className="grid items-start gap-8 lg:grid-cols-[minmax(0,1fr)_20rem]">
        <section aria-labelledby="recent-human-games">
          <h2 id="recent-human-games" className="mb-4 text-xl font-semibold">
            {t('gameArchive.recentGames')}
          </h2>

          {isLoading && (
            <GameListSkeleton label={t('gameArchive.loadingGames')} />
          )}

          {isError && (
            <Card className="border-destructive/40 py-8">
              <CardContent className="text-sm text-muted-foreground">
                {t('gameArchive.loadError')}
              </CardContent>
            </Card>
          )}

          {data && data.games.length === 0 && (
            <Card className="py-8">
              <CardContent className="text-sm text-muted-foreground">
                {t('gameArchive.emptyGames')}
              </CardContent>
            </Card>
          )}

          <ol className="space-y-4">
            {data?.games.map(game => (
              <DirectoryGameCard
                key={game.id}
                game={game}
                language={language}
                t={t}
              />
            ))}
          </ol>

          {data && data.pageCount > 1 && (
            <nav
              className="mt-6 flex items-center justify-between gap-4"
              aria-label={t('gameArchive.pagination.label')}
              data-testid="game-archive-pagination"
            >
              {
              }
              <a
                href={page > 1 ? pageHref(page - 1) : undefined}
                aria-disabled={page <= 1}
                data-testid="game-archive-prev"
                className={cn(
                  'text-sm underline-offset-4 hover:underline',
                  page <= 1 && 'pointer-events-none opacity-40',
                )}
                onClick={(e) => {
                  e.preventDefault()
                  if (page > 1)
                    goToPage(page - 1)
                }}
              >
                {t('gameArchive.pagination.previous')}
              </a>

              <span className="text-sm text-muted-foreground">
                {t('gameArchive.pagination.status', {
                  page: String(data.page),
                  pageCount: String(data.pageCount),
                  total: String(data.totalGames),
                })}
              </span>

              <a
                href={page < data.pageCount ? pageHref(page + 1) : undefined}
                aria-disabled={page >= data.pageCount}
                data-testid="game-archive-next"
                className={cn(
                  'text-sm underline-offset-4 hover:underline',
                  page >= data.pageCount && 'pointer-events-none opacity-40',
                )}
                onClick={(e) => {
                  e.preventDefault()
                  if (page < data.pageCount)
                    goToPage(page + 1)
                }}
              >
                {t('gameArchive.pagination.next')}
              </a>
            </nav>
          )}
        </section>

        <SupporterLeaderboard
          supporters={data?.supporters ?? []}
          playersByGames={data?.playersByGames ?? []}
          language={language}
          t={t}
          isLoading={isLoading}
          isError={isError}
        />
      </div>
    </main>
  )
}

export function DirectoryGameCard({
  game,
  language,
  t,
}: {
  game: DirectoryGame
  language: string
  t: Translate
}) {
  const gamePath = withLanguagePath(
    getStartedGameRoute(game.id, game.gameSetting),
    language,
  )
  const setting = formatDirectoryGameSetting(
    game.gameSetting,
    game.playerCount,
    t,
  )

  return (
    <li>
      <Card className="gap-4 py-5 transition-colors hover:border-primary/50">
        <CardHeader className="gap-2 px-5 sm:px-6">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <CardTitle className="text-lg leading-snug">
                <a
                  href={gamePath}
                  className="inline-flex items-center gap-2 hover:text-primary hover:underline"
                >
                  <Swords className="size-4 shrink-0" aria-hidden />
                  {setting.title}
                </a>
              </CardTitle>
              <CardDescription className="mt-1.5 flex items-center gap-1.5">
                <UsersRound className="size-3.5" aria-hidden />
                {setting.playerCountLabel}
              </CardDescription>
            </div>
            {game.isEnglishFallback && (
              <Badge variant="secondary">
                {t('gameArchive.englishFallback')}
              </Badge>
            )}
          </div>
        </CardHeader>

        <CardContent className="space-y-4 px-5 sm:px-6">
          <p className="text-sm leading-relaxed">{setting.composition}</p>

          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-muted-foreground">
            <span className="inline-flex items-center gap-1.5">
              <UserRound className="size-3.5" aria-hidden />
              {t('gameArchive.playedBy')}{' '}
              <span className="inline-flex flex-wrap gap-x-1.5">
                {game.humanPlayers.map((player, index) => (
                  <span key={player.username}>
                    {index > 0 && <span aria-hidden>, </span>}
                    <a
                      href={buildPublicPlayerProfileUrl(
                        player.username,
                        language,
                      )}
                      className="font-medium text-foreground hover:text-primary hover:underline"
                    >
                      @{player.username}
                    </a>
                  </span>
                ))}
              </span>
            </span>
            <span>{winnerLabel(game.winner, t)}</span>
            <span className="inline-flex items-center gap-1.5">
              <CalendarDays className="size-3.5" aria-hidden />
              {formatDirectoryDate(game.startedAt, language)}
            </span>
          </div>

          <a
            href={gamePath}
            className="inline-flex text-sm font-medium text-primary hover:underline"
          >
            {t('gameArchive.viewReplay')}
          </a>
        </CardContent>
      </Card>
    </li>
  )
}

export function SupporterLeaderboard({
  supporters,
  playersByGames = EMPTY_GAMES_PLAYERS,
  language,
  t,
  isLoading = false,
  isError = false,
}: {
  supporters: DirectorySupporter[]
  playersByGames?: DirectoryGamesPlayer[]
  language: string
  t: Translate
  isLoading?: boolean
  isError?: boolean
}) {
  return (
    <aside
      aria-labelledby="supporter-leaderboard games-played-leaderboard"
      className="lg:sticky lg:top-24"
    >
      <div className="space-y-6">
        <Card className="gap-4 py-5">
          <CardHeader className="px-5">
            <div className="mb-2 flex size-9 items-center justify-center rounded-full bg-rose-500/10 text-rose-500">
              <HeartHandshake className="size-5" aria-hidden />
            </div>
            <CardTitle id="supporter-leaderboard">
              {t('gameArchive.supportersTitle')}
            </CardTitle>
            <CardDescription>
              {t('gameArchive.supportersDescription')}
            </CardDescription>
          </CardHeader>
          <CardContent className="px-5">
            {isError && (
              <p role="alert" className="text-sm text-muted-foreground">
                {t('gameArchive.leaderboardsLoadError')}
              </p>
            )}
            {!isError && isLoading && (
              <LeaderboardSkeleton label={t('gameArchive.loadingSupporters')} />
            )}
            {!isError && !isLoading && supporters.length === 0 && (
              <p className="text-sm text-muted-foreground">
                {t('gameArchive.noSupporters')}
              </p>
            )}
            {!isError && (
              <ol className="divide-y">
                {supporters.map((supporter, index) => (
                  <LeaderboardRow
                    key={supporter.username}
                    rank={index + 1}
                    username={supporter.username}
                    language={language}
                    value={formatUsd(supporter.amountUsdCents, language)}
                  />
                ))}
              </ol>
            )}
          </CardContent>
        </Card>

        <GamesPlayedLeaderboard
          players={playersByGames}
          language={language}
          t={t}
          isLoading={isLoading}
          isError={isError}
        />
      </div>
    </aside>
  )
}

export function GamesPlayedLeaderboard({
  players,
  language,
  t,
  isLoading = false,
  isError = false,
}: {
  players: DirectoryGamesPlayer[]
  language: string
  t: Translate
  isLoading?: boolean
  isError?: boolean
}) {
  return (
    <Card className="gap-4 py-5">
      <CardHeader className="px-5">
        <div className="mb-2 flex size-9 items-center justify-center rounded-full bg-amber-500/10 text-amber-600 dark:text-amber-400">
          <Trophy className="size-5" aria-hidden />
        </div>
        <CardTitle id="games-played-leaderboard">
          {t('gameArchive.gamesLeaderboardTitle')}
        </CardTitle>
        <CardDescription>
          {t('gameArchive.gamesLeaderboardDescription')}
        </CardDescription>
      </CardHeader>
      <CardContent className="px-5">
        {isError && (
          <p role="alert" className="text-sm text-muted-foreground">
            {t('gameArchive.leaderboardsLoadError')}
          </p>
        )}
        {!isError && isLoading && (
          <LeaderboardSkeleton
            label={t('gameArchive.loadingGamesLeaderboard')}
          />
        )}
        {!isError && !isLoading && players.length === 0 && (
          <p className="text-sm text-muted-foreground">
            {t('gameArchive.noRankedPlayers')}
          </p>
        )}
        {!isError && (
          <ol className="divide-y">
            {players.slice(0, 10).map((player, index) => (
              <LeaderboardRow
                key={player.username}
                rank={index + 1}
                username={player.username}
                language={language}
                value={t('gameArchive.gamesPlayed', {
                  count: player.gamesPlayed,
                })}
              />
            ))}
          </ol>
        )}
      </CardContent>
    </Card>
  )
}

function LeaderboardRow({
  rank,
  username,
  language,
  value,
}: {
  rank: number
  username: string
  language: string
  value: string
}) {
  return (
    <li className="flex items-center gap-3 py-3 first:pt-0 last:pb-0">
      <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-semibold text-muted-foreground">
        {rank}
      </span>
      <a
        href={buildPublicPlayerProfileUrl(username, language)}
        className="min-w-0 flex-1 truncate text-sm font-medium hover:text-primary hover:underline"
      >
        @{username}
      </a>
      <span className="shrink-0 text-sm font-semibold tabular-nums">
        {value}
      </span>
    </li>
  )
}

function GameListSkeleton({ label }: { label: string }) {
  return (
    <div className="space-y-4" aria-label={label}>
      {[0, 1, 2].map(index => (
        <div
          key={index}
          className="h-44 animate-pulse rounded-xl border bg-muted/30"
        />
      ))}
    </div>
  )
}

function LeaderboardSkeleton({ label }: { label: string }) {
  return (
    <div className="space-y-4" aria-label={label}>
      {[0, 1, 2, 3].map(index => (
        <div key={index} className="h-7 animate-pulse rounded bg-muted/50" />
      ))}
    </div>
  )
}
